import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { TextField } from 'office-ui-fabric-react';

import { KeyboardKeys } from '@app/AppConstants';
import { TextCell } from './TextCell';
import { ViewMode } from './ViewMode';
import { CellInfo } from 'react-table';

export interface IInlineTextboxCellProps<T extends { id?: string }> {
    value: string;
    row: T;
    fieldKey: keyof T;
    onChange?: (row: T) => void;
    disabled?: boolean;
    required?: boolean;
    editOnDoubleClick?: boolean;
    cellInfo:CellInfo;
}

@observer
export class InlineTextboxCell<T extends { id?: string }> extends React.Component<IInlineTextboxCellProps<T>, {}> {
    store: InlineTextboxCellStore<T>;
    props: IInlineTextboxCellProps<T>;

    constructor(props: IInlineTextboxCellProps<T>) {
        super(props);
        this.props = props;
        this.store = new InlineTextboxCellStore<T>(props);
    }

    componentDidUpdate(prevProps: IInlineTextboxCellProps<T>) {
        if (this.props.row.id !== prevProps.row.id || this.props.value !== prevProps.value || this.props !== prevProps) {
            this.store.updateProps(this.props);
        }
    }

    render() {
        if (this.store.mode === ViewMode.view) {
                return <TextCell value={this.store.value}
                    onDoubleClick={this.props.editOnDoubleClick && this.store.toEditMode || undefined}
                    onClick={!this.props.editOnDoubleClick && this.store.toEditMode || undefined} />;
        } else {
            return (
                <TextField
                    onChange={this.store.onEdit}
                    onKeyDown={this.store.onKeyDown}
                    onBlur={this.store.onBlurHandler}
                    autoFocus={true}
                    value={this.store.value}
                />
            );
        }
    }

    @action
    public clearValue() {
        this.store.value = '';
    }
}

export class InlineTextboxCellStore<T extends { id?: string }> {
    @observable value: string;
    @observable mode: string = ViewMode.view;
    private _initValue: string;
    private _currentProps: IInlineTextboxCellProps<T>;

    constructor(props: IInlineTextboxCellProps<T>) {
        this.updateProps(props);
    }

    @action
    toEditMode = () => {
        if (this._currentProps.disabled) return;
        this.mode = ViewMode.edit;
    };

    @action
    onEdit = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        this.value = newValue || '';
    };

    @action
    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.keyCode === KeyboardKeys.ENTER) {
            this.onBlurHandler();
            event.preventDefault();
        }

        if (event.keyCode === KeyboardKeys.ESCAPE) {
            this.closeEditMode();
        }
    };

    @action
    closeEditMode() {
        this.value = this._initValue || '';
        this.mode = ViewMode.view;
    }

    @action
    onBlurHandler = () => {
        if (this._currentProps.required && !this.value || this._initValue === this.value) {
            this.closeEditMode();
            return;
        }
        this.mode = ViewMode.view;
        this._initValue = this.value;
        const row = this._currentProps.row as unknown as {[key: string]: string};
        row[this._currentProps.fieldKey as string] = this.value || '';
        if (this._currentProps.onChange)
            this._currentProps.onChange(this._currentProps.row);
    };

    @action
    updateProps(props: IInlineTextboxCellProps<T>) {
        const value = props.value || '';
        this._initValue = value;
        this._currentProps = { ...props };
        this.value = value || ''; //`value` prop on `input` should not be null.
    }
}
